import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type ReactElement,
  type ReactNode,
} from 'react';
import type { BoardListState, BoardPageBoard } from '@molt/shared';
import { Button, EmptyState, Skeleton, TextField } from '@/components';
import { announce } from '@/lib/announce';
import { filterGroupsByName, type LandingGroup } from '@/lib/boards-page';
import { cn } from '@/lib/cn';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { WorkspaceCreateForm } from '../workspace/WorkspaceCreateForm';
import { BoardGroup } from './BoardGroup';
import { BoardShortcutSection } from './BoardShortcutSection';

/**
 * The boards landing page — `FE-23.1`, chunk `D2`, agent G. `FR-NOTIF-029`.
 *
 * Presentational, and it owns **exactly one** piece of state: the name filter.
 * That is local UI state with no URL and no server (`entry-surfaces.md` **D9**
 * — *the name filter is client-side; the state toggle is in the URL*).
 * Everything else is props, and `routes/index.tsx` owns the queries, the
 * mutation, the search params, the navigation and the create announcement.
 *
 * ---------------------------------------------------------------------------
 * EMPTY, LOADING AND FAILED ARE THREE RENDERS, NEVER THREE STATES OF ONE
 * ---------------------------------------------------------------------------
 * `entry-surfaces.md` **D5**, fixed:
 *
 *  - **loading** → `Skeleton` in the shape of the content, `aria-busy="true"`
 *    on the region. No indefinite spinner, ever.
 *  - **empty, no workspaces at all** → `EmptyState tone="neutral"
 *    size="panel"`, whose action starts the inline create form. This is
 *    `FE-07.1`'s first-run host, and the form **replaces the EmptyState's body
 *    in place — there is no Dialog on this path**. Cancel restores the
 *    `EmptyState` and returns focus to the button that opened it.
 *  - **empty, but there are workspaces** → the groups render with their own
 *    empty bodies. The page is not empty.
 *  - **failed** → `EmptyState tone="error" size="panel"` (which supplies
 *    `role="alert"` itself), the server's message **verbatim**, and a *Try
 *    again* button.
 *  - **no board matches the filter** → `No boards match "design".` and a
 *    *Clear filter* button.
 *
 * The branch order below puts **failed first**. `FR-EXP-034`'s second edge
 * case is that a failed load must never render the empty copy, and a failed
 * query has `isPending: false` and no data — which is indistinguishable from
 * "this member has nothing" if the empty branch is tested first.
 *
 * ⚠ `EmptyState`'s tones are `'neutral' | 'error'` — **not `'bad'`**, which is
 * `Notice`'s vocabulary. `entry-surfaces.md`'s primitive table says `'bad'`
 * for both and is known wrong; D1 found it and recorded that D2 reads that
 * table next.
 *
 * ⚠ **A 401 is not an error state on this page.** `main.tsx`'s
 * `QueryCache.onError` redirects to `/sign-in?next=…` before this component
 * can render one, and this chunk is that binding's first live caller. Do not
 * add a second handler and do not set `throwOnError` — D1 measured that
 * `throwOnError: false` does not suppress `QueryCache.onError`
 * (`query-core@5.101.4`, `query.js:323`); the exclusion is by query key.
 *
 * ⚠ **And the 401 is still passed through as `error`, deliberately.** The
 * tempting "improvement" is for the route to suppress an `unauthenticated`
 * message so the failed render never flashes during the redirect. It makes
 * things worse: with the error suppressed, `loading` is already `false` (the
 * query settled, unsuccessfully) and `groups` is empty, so the frame renders
 * the **first-run "create your first workspace"** copy at a visitor who is
 * merely signed out. A generic failure for one frame beats first-run copy for
 * one frame. Do not "fix" this into the empty state.
 *
 * ---------------------------------------------------------------------------
 * ⚠ THE CLOSED SURFACE IS A SIXTH RENDER, AND ITS EMPTY STATE IS PAGE-LEVEL
 * ---------------------------------------------------------------------------
 * `FE-08.3`, chunk `D3`. `FR-EXP-012`: *"Closed boards are listed on a
 * dedicated Closed boards surface."* That surface is **this page with
 * `?state=closed`**, so this component learns the state it is rendering and
 * two things follow that do not follow for the open surface:
 *
 *  1. **A group with no closed boards is not rendered at all.** `groups`
 *     arrives left-joined with every workspace the member belongs to
 *     (`joinWorkspaceGroups`), which is right on the open surface — a
 *     workspace with no boards is a place to make one. On the closed surface
 *     it would render *"No boards in this workspace yet."* under a workspace
 *     holding five open boards, which is simply false.
 *  2. **When that leaves nothing, the empty state is the page's, at
 *     `headingLevel={2}`** — *"No closed boards."* — and **not** one level-3
 *     empty body per group. `BoardGroup`'s own docstring records what the `D2`
 *     screenshot pass found when an empty group was given room: on a page with
 *     three workspaces the **emptiest** one became the largest thing on
 *     screen. A member with four workspaces and one closed board would get
 *     three copies of the same sentence. `D3-BRIEF.md` §6.11 makes the heading
 *     rank conditional on nesting (*"`headingLevel={3}` **if** it nests under
 *     a group `<h2>`"*); it does not nest, so it is 2.
 *
 * ⚠ **It is not announced.** The member *navigated* here, which is the case
 * `EmptyState`'s own docstring covers with *"the heading is the
 * announcement"*. The no-match sentence below is announced only because focus
 * stays in the filter field while the page empties underneath it — that
 * exception does not generalise, and a second channel for one event is what
 * `D3-BRIEF.md` §5.7 forbids.
 *
 * ---------------------------------------------------------------------------
 * ⚠ THE STATE TABS ARE A SLOT, AND THE ROUTE FILLS IT
 * ---------------------------------------------------------------------------
 * `BoardStateTabs` renders TanStack `Link`s and therefore needs a router in
 * context; this component is presentational and its tests mount it without
 * one. So `routes/index.tsx` **mounts** the tabs (`D3-BRIEF.md` §6.11) and
 * hands them here as a node, which is also what keeps them inside this page's
 * centred frame instead of floating above it.
 *
 * ⚠ **`tabs` is REQUIRED and is rendered in every branch — including the
 * failed one and the closed-empty one.** On `?state=closed` with nothing
 * closed, the tabs are the only way back to the open list: render them inside
 * `renderBody()`'s loaded branch only and the Closed surface becomes a dead
 * end for anyone who arrived there by link.
 *
 * ---------------------------------------------------------------------------
 * ⚠ THE FILTER REMOVES BOARDS, NOT ONLY GROUPS — AND MOUNTING THIS PAGE IS
 * WHAT FOUND THAT IT DID NOT
 * ---------------------------------------------------------------------------
 * `lib/boards-page.ts`'s `filterGroupsByName` used to drop only a **group**
 * whose boards all failed the match, leaving a surviving group's `boards`
 * untouched — `D2-BRIEF.md` §6.2's docstring implemented exactly as written,
 * with a unit test that asserted the array was not touched. So the brief, the
 * function and its test all agreed, and all three were wrong: a workspace
 * holding *Roadmap* and *Q3 Budget*, filtered for `budget`, kept **both** on
 * screen, which is precisely what `FR-NOTIF-029` AC4 says must not happen.
 *
 * It is fixed **in `filterGroupsByName`**, which filters at both levels now, so
 * the matching rule exists once. This file calls it and nothing else.
 *
 * The transferable part: a pure function's unit test is written from the same
 * sentence as the function, so when the sentence is wrong the test agrees with
 * the code and both are green together. **Only a caller disagrees.**
 *
 * ---------------------------------------------------------------------------
 * WHY THIS COMPONENT CALLS `announce()` AND `routes/profile.tsx` DOES NOT
 * ---------------------------------------------------------------------------
 * `D2-BRIEF.md` §6.7 rule 2 says the route owns every `announce()`, and rule 6
 * says the debounced filter count is announced. **The two cannot both hold**:
 * rule 1 puts the filter state in this component, the fixed props signature
 * has no `onFilterSettled` callback, and a route cannot announce a count it
 * cannot see. The house already has the precedent for the resolution —
 * `SignInPanel` announces its own mode change and
 * `entry-surfaces.composition.test.tsx` asserts it happens *"from the panel
 * and not the route"*. A component may announce a change to state it owns; it
 * may not announce a change to state the route owns. So: the filter count is
 * announced here, and the workspace-created message is announced by the route.
 *
 * ⚠ **One event, one channel (D11).** The failed render is an `EmptyState
 * tone="error"`, which already carries `role="alert"` — so it is **not** also
 * announced. The no-match render is `tone="neutral"`, which carries no live
 * region role at all, so `announce()` is its correct and only channel.
 *
 * The announced no-match sentence is word-for-word the heading beside it, and
 * that is deliberate rather than an oversight. `EmptyState`'s own docstring
 * says of `tone="neutral"` that *"the heading is the announcement"* — which
 * holds for an empty state a member **navigates to**. Here they do not:
 * focus stays in the filter field while the page empties underneath it, and a
 * heading nobody's cursor reaches announces nothing at all. So the sentence is
 * spoken once, politely, by the one channel that reaches a member who is still
 * typing.
 */

/**
 * D11's *"fires once settled"*. Long enough that a member typing `budget`
 * hears one sentence rather than six, short enough that it is not lost.
 */
const FILTER_ANNOUNCE_DELAY_MS = 400;

export interface BoardsLandingProps {
  groups: readonly LandingGroup[];
  starredBoards: readonly BoardPageBoard[];
  recentBoards: readonly BoardPageBoard[];
  /**
   * `FE-08.3` — which list this page is showing. Read from `?state=` by
   * `routes/index.tsx` and narrowed there; this component only branches on it.
   */
  state: BoardListState;
  /**
   * `FE-08.3` — the Open / Closed tabs, mounted by the route because they need
   * a router. **Required**, and rendered in every branch: see the docstring.
   */
  tabs: ReactNode;
  /**
   * `FE-65.2` — route-owned link to the public template gallery.
   *
   * Kept as a slot for the same reason as `tabs`: the route owns TanStack
   * links, while this component owns the page frame.
   */
  templateGalleryLink: ReactNode;
  /** True only while the FIRST load is in flight. */
  loading: boolean;
  /** The server's message, verbatim, or undefined. */
  error?: string;
  onRetry: () => void;
  /** `FE-07.1`'s inline host. */
  creating: boolean;
  onStartCreating: () => void;
  onCancelCreating: () => void;
  onCreateWorkspace: (name: string) => void;
  createBusy?: boolean;
  createError?: string;
  /**
   * The workspace whose group takes focus after a create — its *Create a
   * board* button since `D3`, its `<h2>` when it draws none (`BoardGroup`).
   */
  focusGroupId?: string | null;
  /**
   * `FE-08.1` — chunk `D3`. **This file is the middle of a three-file prop
   * chain** (`routes/index.tsx` → here → `BoardGroup`), and it is named in all
   * three deliberately: naming only the two endpoints leaves the middle to a
   * file nobody was told they owned, which is exactly how `C3` shipped a chunk
   * that was unreachable in the composed application with every suite green.
   *
   * The ids come from `useWorkspaces()`, never from `page.groups` —
   * `D3-BRIEF.md` §5.2.
   */
  creatableWorkspaceIds: ReadonlySet<string>;
  onCreateBoard: (workspaceId: string) => void;
  onToggleBoardStar: (board: BoardPageBoard, nextStarred: boolean) => void;
  onReorderStarredBoard: (
    boardId: string,
    neighbours: { previousBoardId: string | null; nextBoardId: string | null },
  ) => void;
  className?: string;
}

export function BoardsLanding({
  groups,
  starredBoards,
  recentBoards,
  state,
  tabs,
  templateGalleryLink,
  loading,
  error,
  onRetry,
  creating,
  onStartCreating,
  onCancelCreating,
  onCreateWorkspace,
  createBusy,
  createError,
  focusGroupId,
  creatableWorkspaceIds,
  onCreateBoard,
  onToggleBoardStar,
  onReorderStarredBoard,
  className,
}: BoardsLandingProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const [query, setQuery] = useState('');
  const trimmedQuery = query.trim();

  const focusRef = useRef<HTMLElement | null>(null);
  const startCreatingRef = useRef<HTMLButtonElement | null>(null);
  const lastFocusedGroupRef = useRef<string | null>(null);
  const wasCreatingRef = useRef(false);
  const hasFilteredRef = useRef(false);

  /*
   * The closed surface drops the left-joined empty groups before anything else
   * sees them — see the docstring. The open surface keeps every one of them:
   * a workspace with no boards is a place to make one, which is exactly what
   * `FR-PERM-004` AC2 and `FR-EXP-034` ask this page to show.
   */
  const listedGroups = useMemo(
    () =>
      state === 'closed'
        ? groups.filter((group) => group.boards.length > 0)
        : [...groups],
    [groups, state],
  );

  const visibleGroups = useMemo(
    () => filterGroupsByName(listedGroups, trimmedQuery),
    [listedGroups, trimmedQuery],
  );
  const visibleStarredBoards = useMemo(
    () => filterBoardsByName(starredBoards, trimmedQuery),
    [starredBoards, trimmedQuery],
  );
  const visibleRecentBoards = useMemo(
    () => filterBoardsByName(recentBoards, trimmedQuery),
    [recentBoards, trimmedQuery],
  );
  const starredBoardIds = useMemo(
    () => new Set(starredBoards.map((board) => board.id)),
    [starredBoards],
  );
  const matchCount =
    visibleGroups.reduce((total, group) => total + group.boards.length, 0) +
    visibleStarredBoards.length +
    visibleRecentBoards.length;
  const hasVisibleBoards =
    visibleGroups.length > 0 ||
    visibleStarredBoards.length > 0 ||
    visibleRecentBoards.length > 0;

  /*
   * Cancel returns focus to the button that opened the form (D5).
   *
   * `groups.length === 0` is the guard that keeps this from fighting the
   * focus effect below: a *successful* create also flips `creating` to false,
   * and in that commit the groups are no longer empty, so the new heading
   * wins and this does nothing.
   */
  useEffect(() => {
    if (wasCreatingRef.current && !creating && groups.length === 0) {
      startCreatingRef.current?.focus();
    }
    wasCreatingRef.current = creating;
  }, [creating, groups.length]);

  /*
   * §2.3 — focus the new workspace's heading after a create.
   *
   * `visibleGroups` is in the dependency list on purpose: the group arrives in
   * a *later* commit than `focusGroupId` is set in whenever the cache write
   * and the state update do not land together, and an effect keyed on
   * `focusGroupId` alone would find a null ref once and never retry.
   * `lastFocusedGroupRef` is the other half — without it, every unrelated
   * re-render steals focus back, which is the trap `WorkspaceCreateForm`'s
   * docstring records about running `autoFocus` on more than mount.
   */
  useEffect(() => {
    if (focusGroupId === undefined || focusGroupId === null) {
      lastFocusedGroupRef.current = null;
      return;
    }
    if (lastFocusedGroupRef.current === focusGroupId) return;
    const target = focusRef.current;
    if (target === null) return;
    lastFocusedGroupRef.current = focusGroupId;
    target.focus();
  }, [focusGroupId, visibleGroups]);

  /*
   * D11 — the filter count, debounced, announced once it settles.
   *
   * `hasFilteredRef` is what keeps the empty query silent on mount: an
   * announcement of "showing all boards" to a member who has not typed
   * anything is the live region talking to itself.
   */
  useEffect(() => {
    if (trimmedQuery === '' && !hasFilteredRef.current) return;

    const timer = setTimeout(() => {
      if (trimmedQuery === '') {
        hasFilteredRef.current = false;
        announce(t('boards.filterCleared'));
        return;
      }
      hasFilteredRef.current = true;
      announce(
        matchCount === 0
          ? t('boards.filterNoMatches', { query: trimmedQuery })
          : t(
              'boards.filterMatches',
              { count: matchCount, query: trimmedQuery },
              matchCount,
            ),
      );
    }, FILTER_ANNOUNCE_DELAY_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [trimmedQuery, matchCount, t]);

  function renderBody(): ReactElement {
    if (error !== undefined) {
      return (
        <EmptyState
          tone="error"
          size="panel"
          headingLevel={2}
          title={t('boards.loadFailedTitle')}
          body={error}
          action={
            <Button variant="primary" onClick={onRetry}>
              {t('boards.retry')}
            </Button>
          }
        />
      );
    }

    if (loading) {
      return <BoardsLandingPending />;
    }

    /*
     * ⚠ **Before the first-run branch, and the order is the whole point.**
     * `groups.length === 0` is also true on the Closed surface for a member
     * who has workspaces and nothing closed in them — reaching the first-run
     * branch there would tell somebody with four workspaces to *"create your
     * first workspace"*. Same class of mistake as putting the empty branch
     * before the failed one, which the docstring above records.
     */
    if (state === 'closed' && listedGroups.length === 0) {
      return (
        <EmptyState
          tone="neutral"
          size="panel"
          headingLevel={2}
          title={t('boards.noClosedTitle')}
          body={t('boards.noClosedBody')}
        />
      );
    }

    if (groups.length === 0) {
      return creating ? renderFirstRunForm() : renderFirstRunEmpty();
    }

    return (
      <div className="flex flex-col gap-6">
        <TextField
          type="search"
          label={t('boards.filterLabel')}
          value={query}
          onChange={(event) => {
            setQuery(event.currentTarget.value);
          }}
          autoComplete="off"
          wrapperClassName="max-w-xs"
        />
        {!hasVisibleBoards ? (
          <EmptyState
            tone="neutral"
            size="panel"
            headingLevel={2}
            title={t('boards.filterNoMatches', { query: trimmedQuery })}
            action={
              <Button
                variant="primary"
                onClick={() => {
                  setQuery('');
                }}
              >
                {t('boards.clearFilter')}
              </Button>
            }
          />
        ) : (
          <div className="flex flex-col gap-8">
            <BoardShortcutSection
              title={t('boards.starredTitle')}
              listLabel={t('boards.starredList')}
              boards={visibleStarredBoards}
              starredBoardIds={starredBoardIds}
              onToggleStar={onToggleBoardStar}
              onReorderStarredBoard={onReorderStarredBoard}
              reorderable
            />
            <BoardShortcutSection
              title={t('boards.recentTitle')}
              listLabel={t('boards.recentList')}
              boards={visibleRecentBoards}
              starredBoardIds={starredBoardIds}
              onToggleStar={onToggleBoardStar}
            />
            {visibleGroups.map((group) => {
              /*
               * `exactOptionalPropertyTypes`: `focusRef` is declared
               * `focusRef?: RefObject<…>`, so an explicit `undefined` does
               * not satisfy it — only an absent key does. Typed against
               * `BoardGroup`'s own props rather than left as a bare object
               * literal, because a JSX spread bypasses excess-property
               * checking and a typo'd key would compile to nothing at all
               * (`C2` shipped drag-handle plumbing type-checked by exactly
               * this hole).
               */
              const headingProps: Pick<
                ComponentProps<typeof BoardGroup>,
                'focusRef'
              > = group.workspaceId === focusGroupId ? { focusRef } : {};

              return (
                <BoardGroup
                  key={group.workspaceId}
                  group={group}
                  canCreateBoard={creatableWorkspaceIds.has(group.workspaceId)}
                  onCreateBoard={() => {
                    onCreateBoard(group.workspaceId);
                  }}
                  starredBoardIds={starredBoardIds}
                  onToggleBoardStar={onToggleBoardStar}
                  {...headingProps}
                />
              );
            })}
          </div>
        )}
      </div>
    );
  }

  function renderFirstRunEmpty(): ReactElement {
    return (
      <EmptyState
        tone="neutral"
        size="panel"
        headingLevel={2}
        title={t('boards.firstRunTitle')}
        body={t('boards.firstRunBody')}
        action={
          <Button ref={startCreatingRef} variant="primary" onClick={onStartCreating}>
            {t('boards.createWorkspace')}
          </Button>
        }
      />
    );
  }

  /**
   * D4 — the form **replaces the EmptyState's body in place**. Same sheet,
   * same heading rank, no `Dialog`: the member does not lose the page they
   * were looking at to create the thing the page is about.
   */
  function renderFirstRunForm(): ReactElement {
    const formProps: Pick<
      ComponentProps<typeof WorkspaceCreateForm>,
      'busy' | 'error'
    > = {
      ...(createBusy === undefined ? {} : { busy: createBusy }),
      ...(createError === undefined ? {} : { error: createError }),
    };

    return (
      <EmptyState
        tone="neutral"
        size="panel"
        headingLevel={2}
        title={t('boards.createFirstWorkspace')}
        body={
          <WorkspaceCreateForm
            autoFocus
            onSubmit={onCreateWorkspace}
            onCancel={onCancelCreating}
            className="text-left"
            {...formProps}
          />
        }
      />
    );
  }

  return (
    <div className={cn('mx-auto flex w-full max-w-6xl flex-col gap-6 py-8', className)}>
      {/*
        The `h1` is the page's, not a primitive's: `EmptyState.headingLevel`
        accepts only `2 | 3 | 4` because a primitive cannot know its place in
        the outline, so every branch below opens at level 2 under this one.
        `BoardGroup` supplies the level 2 on the loaded branch. §7 fixes the
        text as `Boards`.
      */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-2xl font-semibold tracking-tight text-ink">
          {t('boards.title')}
        </h1>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {templateGalleryLink}
          {/*
            OUTSIDE `renderBody()`, deliberately — the tabs render in the failed,
            loading, first-run and closed-empty branches too. See the docstring:
            on `?state=closed` with nothing closed they are the only way back.
          */}
          {tabs}
        </div>
      </div>
      {renderBody()}
    </div>
  );
}

function filterBoardsByName(
  boards: readonly BoardPageBoard[],
  query: string,
): BoardPageBoard[] {
  const trimmed = query.trim();
  if (trimmed === '') return [...boards];
  const needle = trimmed.toLowerCase();
  return boards.filter((board) => board.title.toLowerCase().includes(needle));
}

/**
 * The loaded page's own geometry — a filter row, then a heading and a row of
 * tiles per group — so the page does not jump when the real content arrives.
 * `Skeleton` is `aria-hidden` by construction; `aria-busy` is the silent
 * signal, and there is deliberately no spinner and no announcement.
 */
function BoardsLandingPending(): ReactElement {
  return (
    <div aria-busy="true" className="flex flex-col gap-8">
      <Skeleton shape="line" width="16rem" />
      {[0, 1].map((index) => (
        <div key={index} className="flex flex-col gap-3">
          <Skeleton shape="line" width="10rem" />
          <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4">
            <Skeleton shape="block" />
            <Skeleton shape="block" />
            <Skeleton shape="block" />
          </div>
        </div>
      ))}
    </div>
  );
}
